import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { asset } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const EASE_EXPRESSIVE = [0.76, 0, 0.24, 1];

/** True when motion should be skipped (reduced-motion users or ?nomotion for QA). */
export function useStill() {
  const reduce = useReducedMotion();
  const [nomotion, setNomotion] = useState(false);
  useEffect(() => {
    setNomotion(typeof window !== "undefined" && window.location.search.includes("nomotion"));
  }, []);
  return reduce || nomotion;
}

/** Fade + rise reveal when scrolled into view. */
export function Reveal({ children, delay = 0, y = 40, className = "", once = true }) {
  const still = useStill();
  if (still) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-10% 0px -10% 0px" }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Masked line-by-line reveal. Pass an array of strings (lines).
 * Each line sits in an overflow-hidden wrapper and rises into place.
 */
export function LineReveal({ lines = [], className = "", delay = 0, stagger = 0.12, animateOnLoad = true }) {
  const ref = useRef(null);
  const still = useStill();
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (still) return;
    if (animateOnLoad) {
      const t = setTimeout(() => setShown(true), 80);
      return () => clearTimeout(t);
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [animateOnLoad, still]);

  if (still) {
    return (
      <span className={className} style={{ display: "block" }}>
        {lines.map((line, i) => (
          <span key={i} style={{ display: "block" }}>{line}</span>
        ))}
      </span>
    );
  }

  return (
    <span ref={ref} className={className} data-shown={shown ? "1" : "0"} style={{ display: "block" }}>
      {lines.map((line, i) => (
        <span key={i} style={{ display: "block", overflow: "hidden", paddingBottom: "0.06em" }}>
          <span
            className={`aria-line ${shown ? "is-in" : ""}`}
            style={{ animationDelay: `${delay + i * stagger}s` }}
          >
            {line}
          </span>
        </span>
      ))}
    </span>
  );
}

/** Image inside an overflow-hidden frame with subtle parallax on scroll. */
export function ParallaxImage({ src, alt = "", className = "", imgClassName = "", strength = 60, ...rest }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-strength, strength]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`} {...rest}>
      <motion.img
        src={asset(src)}
        alt={alt}
        loading="lazy"
        style={{ y }}
        className={`h-[115%] w-full object-cover ${imgClassName}`}
      />
    </div>
  );
}

/** Clipped-frame reveal: frame expands while image de-scales. */
export function ClippedImage({ src, alt = "", className = "", imgClassName = "", aspect = "aspect-[4/5]" }) {
  const still = useStill();
  if (still) {
    return (
      <div className={`relative overflow-hidden ${aspect} ${className}`}>
        <img src={asset(src)} alt={alt} loading="lazy" className={`h-full w-full object-cover ${imgClassName}`} />
      </div>
    );
  }
  return (
    <div className={`relative overflow-hidden ${aspect} ${className}`}>
      <motion.div
        className="h-full w-full"
        initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
        whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
        viewport={{ once: true, margin: "-12% 0px" }}
        transition={{ duration: 1.3, ease: EASE_EXPRESSIVE }}
      >
        <motion.img
          src={asset(src)}
          alt={alt}
          loading="lazy"
          initial={{ scale: 1.25 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true, margin: "-12% 0px" }}
          transition={{ duration: 1.6, ease: EASE_EXPRESSIVE }}
          className={`h-full w-full object-cover ${imgClassName}`}
        />
      </motion.div>
    </div>
  );
}

/** Slow editorial marquee. */
export function Marquee({ items = [], className = "" }) {
  const doubled = [...items, ...items];
  return (
    <div className={`w-full overflow-hidden ${className}`} aria-hidden="true">
      <div className="aria-marquee-track">
        {doubled.map((it, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8 font-display italic text-aria-ink/90 text-5xl md:text-7xl lg:text-8xl">
            {it}
            <span className="inline-block h-2 w-2 rounded-full bg-aria-brass align-middle" />
          </span>
        ))}
      </div>
    </div>
  );
}

export { EASE, EASE_EXPRESSIVE };
